import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';

// Pages
import HomePage from './pages/HomePage';
import ChefSignUp from './pages/ChefSignUp';
import CustomerSignUp from './pages/CustomerSignUp';
import Login from './pages/Login';
import ChefDashboard from './pages/ChefDashboard';
import CustomerDashboard from './pages/CustomerDashboard';
import AdminDashboard from './pages/AdminDashboard';
import BartenderDashboard from './pages/BartenderDashboard';

// Components
import InstallPrompt from './components/InstallPrompt';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
export const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

// Configure axios defaults
axios.defaults.baseURL = API;

function App() {
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem('chef2go_user');
    return stored ? JSON.parse(stored) : null;
  });
  
  const [token, setToken] = useState(() => localStorage.getItem('chef2go_token'));

  const handleLogin = (userData, authToken) => {
    setUser(userData);
    setToken(authToken);
    localStorage.setItem('chef2go_user', JSON.stringify(userData));
    localStorage.setItem('chef2go_token', authToken);
    
    // Set axios authorization header
    axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
  };

  const handleLogout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('chef2go_user');
    localStorage.removeItem('chef2go_token');
    delete axios.defaults.headers.common['Authorization'];
  };

  // Set auth header if token exists
  React.useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, [token]);

  return (
    <BrowserRouter>
      <div className="min-h-screen bg-gray-50">
        {/* PWA Install Prompt */}
        <InstallPrompt />
        {/* Navigation */}
        <nav className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-16 items-center">
              <Link to="/" className="text-2xl font-bold text-orange-600">
                Chef2Go Now 🍳
              </Link>
              
              <div className="flex items-center gap-4">
                {/* Social Media Links */}
                <a 
                  href="https://facebook.com/chef2gonow" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-blue-600 transition"
                  title="Follow us on Facebook"
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                <a 
                  href="https://instagram.com/chef2_Go_Now" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-pink-600 transition"
                  title="Follow us on Instagram"
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </a>
                
                {user ? (
                  <>
                    <span className="text-sm text-gray-600">Welcome, <strong>{user.name}</strong> ({user.role})</span>
                    <button
                      onClick={handleLogout}
                      className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                      data-testid="logout-button"
                    >
                      Logout
                    </button>
                  </>
                ) : (
                  <>
                    <Link to="/login" className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700" data-testid="login-link">
                      Login
                    </Link>
                    <Link to="/signup/chef" className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700" data-testid="chef-signup-link">
                      Become a Chef
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        </nav>

        {/* Routes */}
        <Routes>
          <Route path="/" element={<HomePage user={user} />} />
          <Route path="/signup/chef" element={<ChefSignUp onLogin={handleLogin} />} />
          <Route path="/signup/customer" element={<CustomerSignUp onLogin={handleLogin} />} />
          <Route path="/login" element={<Login onLogin={handleLogin} />} />
          
          {/* Protected Routes */}
          <Route
            path="/chef/dashboard"
            element={user && user.role === 'chef' ? <ChefDashboard user={user} token={token} /> : <Navigate to="/login" />}
          />
          <Route
            path="/bartender/dashboard"
            element={user && user.role === 'bartender' ? <BartenderDashboard user={user} token={token} /> : <Navigate to="/login" />}
          />
          <Route
            path="/customer/dashboard"
            element={user && user.role === 'customer' ? <CustomerDashboard user={user} token={token} /> : <Navigate to="/login" />}
          />
          <Route
            path="/admin/dashboard"
            element={user && user.role === 'admin' ? <AdminDashboard user={user} token={token} /> : <Navigate to="/login" />}
          />
        </Routes>

        {/* Footer */}
        <footer className="bg-gray-900 text-white mt-auto">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Brand */}
              <div>
                <h3 className="text-2xl font-bold text-orange-500 mb-4">Chef2Go Now 🍳</h3>
                <p className="text-gray-400 mb-4">
                  Professional private chefs delivered to your home. Book experienced chefs for your next event.
                </p>
                <div className="flex gap-4">
                  <a 
                    href="https://facebook.com/chef2gonow" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-white transition"
                    aria-label="Facebook"
                  >
                    <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </a>
                  <a 
                    href="https://instagram.com/chef2_Go_Now" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-white transition"
                    aria-label="Instagram"
                  >
                    <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                  </a>
                </div>
              </div>

              {/* Quick Links */}
              <div>
                <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
                <ul className="space-y-2">
                  <li><Link to="/" className="text-gray-400 hover:text-white transition">Home</Link></li>
                  <li><Link to="/signup/customer" className="text-gray-400 hover:text-white transition">Book a Chef</Link></li>
                  <li><Link to="/signup/chef" className="text-gray-400 hover:text-white transition">Become a Chef</Link></li>
                  <li><Link to="/login" className="text-gray-400 hover:text-white transition">Login</Link></li>
                </ul>
              </div>

              {/* Contact */}
              <div>
                <h4 className="text-lg font-semibold mb-4">Get In Touch</h4>
                <ul className="space-y-2 text-gray-400">
                  <li>📧 Email: info@chef2gonow.com</li>
                  <li>🌐 Website: chef2gonow.com</li>
                  <li>💼 For Business Inquiries</li>
                </ul>
              </div>
            </div>

            <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
              <p>&copy; {new Date().getFullYear()} Chef2Go Now. All rights reserved.</p>
              <p className="mt-2 text-sm">
                Follow us on <a href="https://facebook.com/chef2gonow" target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:text-orange-400">Facebook</a> and <a href="https://instagram.com/chef2_Go_Now" target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:text-orange-400">Instagram</a>
              </p>
            </div>
          </div>
        </footer>
      </div>
    </BrowserRouter>
  );
}

export default App;
